import { useState, useEffect } from 'react';
import api from '../../utils/api';

// Shared connected ad-account access — one fetch + one label format for every
// surface that lets a user pick a Google Ads customer or a Meta ad account
// (node editor, Data Sources, workspace defaults, Agents → Configuration).
//
// GET /connected-accounts shape:
//   google:   { connected, accounts: [{ id, label: "<id> — <name>" }] }
//   facebook: { connected, accounts: [{ id: "act_…", name, pixels: [{ id, name }] }] }
// The two payloads differ: Google pre-joins its label server-side, Meta exposes a
// separate `name`. Rendering `a.label || a.id` — as several call sites used to —
// therefore degrades every Meta option to a bare `act_123`.

const EMPTY = { connected: false, accounts: [] };
const NO_LOCK = { googleAccountId: null, metaAccountId: null, metaPixelId: null };

/**
 * Fetch the store's connected Google/Meta ad accounts once per mount, plus the
 * current workspace's `lock`.
 *
 * The account list is always the shop's FULL inventory — the server ships the
 * lock alongside it rather than filtering, because these pickers fall back to a
 * free-text account box when the list is empty, and a filtered-to-nothing list
 * would open the least locked control of all.
 *
 * `all: true` asks for the unlocked view (the workspace form, which must see
 * every account in order to choose the workspace's).
 */
export function useConnectedAdAccounts({ enabled = true, all = false } = {}) {
  const [google, setGoogle] = useState(EMPTY);
  const [facebook, setFacebook] = useState(EMPTY);
  const [lock, setLock] = useState(NO_LOCK);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    api.get('/connected-accounts', all ? { params: { all: 1 } } : undefined)
      .then(({ data }) => {
        if (cancelled) return;
        setGoogle({ connected: !!data?.google?.connected, accounts: data?.google?.accounts || [] });
        setFacebook({ connected: !!data?.facebook?.connected, accounts: data?.facebook?.accounts || [] });
        setLock(data?.lock || NO_LOCK);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setGoogle(EMPTY);
        setFacebook(EMPTY);
        setLock(NO_LOCK);
        setError(err?.response?.data?.error || 'Could not load connected ad accounts');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, all]);

  return { google, facebook, lock, loading, error };
}

/**
 * What the current workspace fixes for one platform, or null when nothing is
 * fixed (account-level scope, or a workspace with no default set).
 * @returns {{ accountId: string, pixelId: string | null } | null}
 */
export function adsLockFor(lock, platform) {
  if (!lock) return null;
  if (platform === 'meta') {
    return lock.metaAccountId ? { accountId: lock.metaAccountId, pixelId: lock.metaPixelId || null } : null;
  }
  return lock.googleAccountId ? { accountId: lock.googleAccountId, pixelId: null } : null;
}

/** 'meta' for Meta ads inputs, 'google' otherwise. Accepts an input `source`. */
export function adPlatformOf(source) {
  return source === 'meta_ads_read' ? 'meta' : 'google';
}

/** Human label for one account option: "Name (act_123)" for Meta, "<id> — <name>" for Google. */
export function adAccountLabel(platform, account) {
  if (!account) return '';
  if (platform === 'meta') return account.name ? `${account.name} (${account.id})` : account.id;
  return account.label || account.id;
}

/** `<Select>`-ready options, with a leading placeholder row. */
export function adAccountOptions(platform, accounts, placeholder = '— Select account —') {
  return [
    { value: '', label: placeholder },
    ...(accounts || []).map((a) => ({ value: a.id, label: adAccountLabel(platform, a) })),
  ];
}
